import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import RecipeForm from '../components/RecipeForm';
import Navigation from '../components/Navigation';
import { mainContentClassName } from '../context/NavContext';
import { getImageUrl } from '../utils/imageUtils';

function RecipeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [usageStats, setUsageStats] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);

  useEffect(() => {
    fetchRecipe();
  }, [id]);

  useEffect(() => {
    if (recipe && !isEditing) {
      fetchUsageStats();
    }
  }, [recipe, id, isEditing]);

  const fetchRecipe = async () => {
    try {
      const response = await axios.get(`/api/recipes/${id}`);
      setRecipe(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching recipe:', error);
      setLoading(false);
    }
  };

  const fetchUsageStats = async () => {
    setLoadingUsage(true);
    try {
      const response = await axios.get(`/api/recipes/${id}/usage`);
      setUsageStats(response.data);
    } catch (error) {
      console.error('Error fetching usage stats:', error);
      setUsageStats(null);
    } finally {
      setLoadingUsage(false);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      await axios.put(`/api/recipes/${id}`, formData);
      await fetchRecipe(); // Refresh the recipe data
      setIsEditing(false); // Exit edit mode
    } catch (error) {
      console.error('Error updating recipe:', error);
      alert('Failed to update recipe. Please try again.');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this recipe? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`/api/recipes/${id}`);
      navigate('/recipes');
    } catch (error) {
      console.error('Error deleting recipe:', error);
      alert('Failed to delete recipe. Please try again.');
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  if (!recipe) {
    return <div className="p-8 text-center">Recipe not found</div>;
  }

  return (
    <>
      <div 
        className="fixed inset-0"
        style={{
          backgroundImage: 'url(/img/add-background.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          zIndex: -1,
          minHeight: '100vh',
          height: '100%'
        }}
      />
      <Navigation />
      <div className={mainContentClassName}>
        <div className="max-w-4xl mx-auto bg-white/60 backdrop-blur-sm rounded-lg shadow-lg overflow-hidden">
        {isEditing ? (
          <div className="p-4 sm:p-6 lg:p-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">Edit Recipe</h1>
              <button
                onClick={handleCancel}
                className="px-4 sm:px-6 py-2 sm:py-3 bg-stone-600 text-white rounded-lg text-sm sm:text-base font-semibold hover:bg-stone-700 transition-colors shadow-md w-full sm:w-auto"
              >
                Cancel
              </button>
            </div>
            <RecipeForm recipe={recipe} onSubmit={handleSubmit} onCancel={handleCancel} />
          </div>
        ) : (
          <>
            {recipe.imageUrl && (
              <img
                src={getImageUrl(recipe.imageUrl)}
                alt={recipe.title}
                className="w-full h-64 object-cover"
              />
            )}
            <div className="p-4 sm:p-6 lg:p-8">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">{recipe.title}</h1>
                    {recipe.rating && (
                      <div className="flex items-center gap-1">
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <span
                              key={i}
                              className={`text-2xl ${
                                i < recipe.rating ? 'text-yellow-400' : 'text-gray-300'
                              }`}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                        <span className="text-lg font-semibold text-gray-700 ml-1">
                          ({recipe.rating}/5)
                        </span>
                      </div>
                    )}
                  </div>
                  {recipe.description && (
                    <p className="text-gray-600 text-lg">{recipe.description}</p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-sky-600 text-white rounded-lg text-sm sm:text-base font-semibold hover:bg-sky-700 transition-colors shadow-md flex-1 sm:flex-none"
                  >
                    Edit
                  </button>
                  <button
                    onClick={handleDelete}
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-red-600 text-white rounded-lg text-sm sm:text-base font-semibold hover:bg-red-700 transition-colors shadow-md flex-1 sm:flex-none"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => navigate('/recipes')}
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-stone-600 text-white rounded-lg text-sm sm:text-base font-semibold hover:bg-stone-700 transition-colors shadow-md flex-1 sm:flex-none"
                  >
                    Back
                  </button>
                </div>
              </div>

          <div className="grid grid-cols-2 gap-4 mb-6 sm:mb-8 pb-6 sm:pb-8 border-b">
            <div className="text-center">
              <p className="text-xl sm:text-2xl font-bold text-orange-500">{recipe.totalTime || (recipe.prepTime && recipe.cookTime ? recipe.prepTime + recipe.cookTime : 'N/A')}</p>
              <p className="text-sm sm:text-base text-gray-600">Duration (min)</p>
            </div>
            <div className="text-center">
              <p className="text-xl sm:text-2xl font-bold text-orange-500">{recipe.servings}</p>
              <p className="text-sm sm:text-base text-gray-600">Servings</p>
            </div>
          </div>

          <div className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">Ingredients</h2>
            <ul className="space-y-2">
              {recipe.ingredients.map((ingredient, idx) => (
                <li key={idx} className="flex items-center gap-2">
                  <span className="text-orange-500">•</span>
                  <span className="font-semibold">{ingredient.quantity} {ingredient.unit}</span>
                  <span>{ingredient.name}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">Instructions</h2>
            <ol className="space-y-4">
              {recipe.instructions.map((instruction, idx) => (
                <li key={idx} className="flex gap-4">
                  <span className="flex-shrink-0 w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold">
                    {idx + 1}
                  </span>
                  <span className="flex-1">{instruction}</span>
                </li>
              ))}
            </ol>
          </div>

          {recipe.nutrition && (recipe.nutrition.calories || recipe.nutrition.protein || recipe.nutrition.carbs || recipe.nutrition.fat) && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4">Nutrition Facts (per serving)</h2>
              <div className="bg-orange-50 rounded-lg p-6 border-2 border-orange-200">
                <div className="mb-4 pb-4 border-b-2 border-orange-300">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-xl">Calories</span>
                    <span className="font-bold text-2xl text-orange-600">
                      {recipe.nutrition.calories || '-'}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span><strong>Total Fat:</strong></span>
                    <span>{recipe.nutrition.fat ? `${recipe.nutrition.fat}g` : '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span><strong>Total Carbohydrate:</strong></span>
                    <span>{recipe.nutrition.carbs ? `${recipe.nutrition.carbs}g` : '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="ml-4">Fiber:</span>
                    <span>{recipe.nutrition.fiber ? `${recipe.nutrition.fiber}g` : '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="ml-4">Sugar:</span>
                    <span>{recipe.nutrition.sugar ? `${recipe.nutrition.sugar}g` : '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span><strong>Protein:</strong></span>
                    <span>{recipe.nutrition.protein ? `${recipe.nutrition.protein}g` : '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span><strong>Sodium:</strong></span>
                    <span>{recipe.nutrition.sodium ? `${recipe.nutrition.sodium}mg` : '-'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {recipe.tags && recipe.tags.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-4">Tags</h2>
              <div className="flex flex-wrap gap-2">
                {recipe.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 bg-orange-100 text-orange-800 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {recipe.seasons && recipe.seasons.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-4">Seasons</h2>
              <div className="flex flex-wrap gap-2">
                {recipe.seasons.map((season, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 bg-green-100 text-green-800 rounded-full capitalize"
                  >
                    {season}
                  </span>
                ))}
              </div>
            </div>
          )}
          {(!recipe.seasons || recipe.seasons.length === 0) && (
            <div>
              <h2 className="text-2xl font-bold mb-4">Seasons</h2>
              <p className="text-gray-500 italic">Available year-round</p>
            </div>
          )}

          {/* Usage Statistics */}
          <div className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">Usage Statistics</h2>
            {loadingUsage ? (
              <p className="text-gray-500 italic">Loading usage statistics...</p>
            ) : usageStats ? (
              <div className="bg-blue-50 rounded-lg p-6 border-2 border-blue-200">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-700">Used in last {usageStats.period?.value} {usageStats.period?.unit}:</span>
                    <span className="font-bold text-xl text-blue-600">{usageStats.count} {usageStats.count === 1 ? 'time' : 'times'}</span>
                  </div>
                  {usageStats.lastUsed ? (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-gray-700">Last used:</span>
                        <span className="text-gray-600">{new Date(usageStats.lastUsed).toLocaleDateString()}</span>
                      </div>
                      {usageStats.daysSinceLastUse !== null && (
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-gray-700">Days since last use:</span>
                          <span className="text-gray-600">{usageStats.daysSinceLastUse} {usageStats.daysSinceLastUse === 1 ? 'day' : 'days'}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-700">Last used:</span>
                      <span className="text-gray-600 italic">Never</span>
                    </div>
                  )}
                  {usageStats.count === 0 && (
                    <div className="mt-3 pt-3 border-t border-blue-300">
                      <p className="text-sm text-blue-700 font-medium">
                        ⭐ This recipe will be prioritized in meal plan generation since it hasn't been used recently.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-gray-500 italic">Unable to load usage statistics</p>
            )}
          </div>
            </div>
          </>
        )}
      </div>
      </div>
    </>
  );
}

export default RecipeDetail;